import {findTenantByTenantCode} from "@/tenantConfig"

if (!document.getElementById('theme-keyframes')) {
  const style = document.createElement('style')
  style.id = 'theme-keyframes'
  style.textContent = '@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }'
  document.head.appendChild(style)
}


export const defaultPallet = {
  colour1: "#000",
  colour2: "#FFF",
  colour3: "#17BC27"
}

export const getTheme = tenantCode => {
  const config = findTenantByTenantCode(tenantCode)
  return config !== undefined ? config.pallet : defaultPallet
}

export function generateTheme(tenantCode) {
  let config = findTenantByTenantCode(tenantCode)
  if (!config) config = findTenantByTenantCode("sh")
  const pallet = config?.pallet || defaultPallet
  
  // Main color properties
  const colors = {
    debit: "#DEA99E",
    credit: "#24999b",
    success: "#5f9f63",
    warning: "#F8B71D",
    alert: "#C13426",
    borderColor1: "#62666B",
    borderColor2: "#000000",
    fontColor1: "#4a505d",
    fontColor2: "#FCFCFC",
    fontColor3: "#F8B71D",
    fontColor4: "#8F969C",
    fontColor5: "#7B7D7D",
    fontColor6: "#a8b0b7",
    linkColor: "#24999B",
    linkColor2: "#7096B5",
    linkColor3: "#57BFF7",
    backgroundColor1: "#FFF",
    backgroundColor2: "#7096B5",
    backgroundColor3: "#4a505d",
    backgroundColor4: "#CACFD2",
    backgroundColor5: "#24999B",
    backgroundColor6: "#F2F3F4",
    backgroundColor7: "#f6f6f6",
    alertBackground: "#fef2f2",
    successBackground: "#f0fdf4",
    alertBorder: "#fecaca",
    successBorder: "#bbf7d0",
  }
  
  return {
    logo: config?.logo,
    logoWidth: config?.logoWidth,
    logoHeight: config?.logoHeight,
    ...colors,
    
    brand: {
      primary: pallet.colour1,
      secondary: pallet.colour2,
      tertiary: pallet.colour3
    },
    spacing: {
      cardPadding: "20px",
      cardGap: "16px",
      sectionGap: "24px",
      iconSpacing: "12px",
      elementSpacing: "8px"
    },
    dimensions: {
      borderRadius: "12px",
      iconRadius: "8px",
      borderWidth: "1px",
      emphasizedBorderWidth: "2px",
      iconContainerSize: "40px"
    },
    typography: {
      cardTitle: {
        fontSize: "18px",
        fontWeight: 600,
        lineHeight: "1.3"
      },
      cardDescription: {
        fontSize: "14px",
        fontWeight: 400,
        lineHeight: "1.5"
      },
      buttonText: {
        fontSize: "16px",
        fontWeight: 600,
        lineHeight: "1"
      },
      benefitText: {
        fontSize: "14px",
        fontWeight: 400,
        lineHeight: "1.4"
      }
    },
    shadows: {
      none: "none",
      emphasized: `0 2px 8px ${pallet.colour1}15`,
      hover: `0 4px 12px ${pallet.colour1}20`
    },
    transitions: {
      default: "all 0.2s ease",
      fast: "width 0.3s ease"
    },
    animations: {
      spin: "spin 1s linear infinite"
    },
    buttonStyle: {
      cursor: "pointer",
      borderRadius: 8,
      paddingTop: 10,
      paddingRight: 10,
      paddingBottom: 10,
      paddingLeft: 10,
      borderStyle: 'solid',
      borderWidth: 1,
      color: pallet.colour2,
      backgroundColor: pallet.colour1,
      userSelect: "none",
      borderColor: pallet.colour1
    },
    dotStyle: {
      base: {
        width: 18,
        height: 18,
        borderRadius: 50,
        backgroundColor: pallet.colour2,
        border: `solid 1px ${pallet.colour1}`,
        marginRight: 5
      },
      selected: {
        backgroundColor: pallet.colour1
      }
    },
    uploadFilesStyle: {
      hideFileInput: {
        width: 0.1,
        height: 0.1,
        opacity: 0,
        overflow: "hidden",
        position: "absolute",
        zIndex: -1
      },
      button: {
        display: "grid",
        gridGap: 10,
        alignItems: "center",
        gridTemplateColumns: "auto auto",
        color: pallet.colour2,
        fontWeight: 600,
        minWidth: 200,
        minHeight: 40,
        cursor: "pointer",
        padding: 10,
        borderStyle: 'solid',
        borderWidth: 1,
        borderColor: pallet.colour1,
        backgroundColor: pallet.colour1,
        borderRadius: 10,
      },
      icon: {
        justifySelf: "end",
      },
      text: {
        justifySelf: "start",
      }
    },
    numberedDot: {
      base: {
        width: 25,
        height: 25,
        borderRadius: 50,
        backgroundColor: "#b7b6b6",
        color: pallet.colour2,
        border: `solid 2px ${colors.borderColor2}`,
        marginRight: 5
      },
      selected: {
        border: `solid 2px ${pallet.colour1}`,
        backgroundColor: pallet.colour1,
      },
      complete: {
        color: pallet.colour3,
        border: `solid 2px ${pallet.colour3}`,
      }
    },
    roundButton: {
      base: {
        minWidth: 25,
        minHeight: 25,
        borderRadius: 50,
        backgroundColor: pallet.colour2,
        border: `solid 1px ${pallet.colour1}`,
        marginRight: 5,
        cursor: "pointer"
      },
      inner: {
        width: 15,
        height: 15,
        borderRadius: 50,
        backgroundColor: pallet.colour1
      }
    },
    linkStyle: {
      color: colors.fontColor2,
      textDecoration: "none",
      fontSize: 15,
      fontWeight: "normal"
    },
    title: {
      fontSize: 40,
      fontWeight: 300,
      color: colors.fontColor1,
      margin: 0
    },
    h2: {
      display: "block",
      color: colors.fontColor1,
      fontSize: 24,
      margin: 0,
      fontWeight: 700
    },
    h3: {
      display: "block",
      color: colors.fontColor1,
      fontSize: 18.72,
      margin: 0,
      fontWeight: 700
    },
    accountCard: {
      container: {
        padding: "16px",
        borderRadius: "8px",
        cursor: "pointer",
        transition: "all 0.2s ease",
        marginBottom: "12px",
        alignItems: "center"
      },
      selected: {
        border: `2px solid ${pallet.colour1}`,
        backgroundColor: `${pallet.colour1}05`
      },
      unselected: {
        border: `1px solid ${colors.borderColor1}40`,
        backgroundColor: "#ffffff"
      },
      iconContainer: {
        borderRadius: "10px",
        padding: "10px",
        marginRight: "16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      },
      iconSelected: {
        backgroundColor: pallet.colour1
      },
      iconUnselected: {
        backgroundColor: `${pallet.colour1}15`
      },
      iconColorSelected: colors.fontColor2,
      iconColorUnselected: pallet.colour1,
      title: {
        fontWeight: 600,
        fontSize: "16px",
        color: colors.fontColor1,
        marginBottom: "4px"
      },
      subtitle: {
        fontSize: "14px",
        color: colors.fontColor4
      },
      checkbox: {
        width: "24px",
        height: "24px",
        borderRadius: "50%",
        border: `2px solid ${colors.borderColor1}40`,
        backgroundColor: "#ffffff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "all 0.2s ease"
      },
      checkboxSelected: {
        border: `2px solid ${pallet.colour1}`,
        backgroundColor: pallet.colour1
      },
      checkIconColor: colors.fontColor2
    },
    bankCard: {
      container: {
        backgroundColor: colors.backgroundColor1,
        borderRadius: "12px",
        cursor: "pointer",
        minHeight: "90px",
        padding: "12px 8px",
        WebkitTapHighlightColor: "rgba(0,0,0,0.1)",
        width: "100%",
        position: "relative"
      },
      secure: {
        border: `2px solid ${pallet.colour1}`,
        boxShadow: `0 4px 12px ${pallet.colour1}20, 0 0 0 1px ${pallet.colour1}10`
      },
      normal: {
        border: `1px solid ${colors.borderColor1}`,
        boxShadow: `0 0 0 1px ${colors.borderColor1}20`
      },
      logoContainer: {
        flex: 1,
        padding: "8px"
      },
      logo: {
        maxWidth: "72px",
        maxHeight: "32px",
        objectFit: "contain"
      },
      bankNameContainer: {
        fontWeight: 600,
        textAlign: "center",
        paddingTop: "4px"
      },
      bankNameSecure: {
        color: pallet.colour1
      },
      bankNameNormal: {
        color: colors.fontColor1
      }
    },
    inputField: {
      container: {
        width: "100%"
      },
      input: {
        padding: "16px",
        borderRadius: "8px",
        backgroundColor: colors.backgroundColor1,
        color: colors.fontColor1,
        outline: "none",
        width: "100%",
        minHeight: "52px",
        boxSizing: "border-box",
        transition: "all 0.2s ease",
        WebkitAppearance: "none",
        MozAppearance: "textfield"
      },
      borderDefault: {
        border: `1px solid ${pallet.colour1}40`,
        borderWidth: "1px"
      },
      borderFocused: {
        border: `2px solid ${pallet.colour1}`,
        borderWidth: "2px"
      },
      borderError: {
        border: `2px solid ${colors.alert}`,
        borderWidth: "2px"
      },
      borderSuccess: {
        border: `2px solid ${colors.success}`,
        borderWidth: "2px"
      },
      helpText: {
        container: {
          fontSize: "12px",
          color: colors.fontColor4,
          marginBottom: "8px",
          padding: "8px",
          backgroundColor: colors.backgroundColor6,
          borderRadius: "6px",
          border: `1px solid ${pallet.colour1}20`
        }
      },
      footer: {
        container: {
          justifyContent: "space-between",
          marginTop: "8px",
          minHeight: "20px"
        },
        errorText: {
          fontSize: "12px",
          fontWeight: "400",
          color: colors.alert
        },
        characterCount: {
          fontSize: "12px",
          fontWeight: "400",
          color: colors.fontColor4,
          marginLeft: "auto"
        }
      }
    },
    selectionCard: {
      container: {
        padding: "16px",
        borderRadius: "8px",
        cursor: "pointer",
        transition: "all 0.2s ease",
        marginBottom: "8px",
        position: "relative",
        backgroundColor: colors.backgroundColor1
      },
      variants: {
        default: {
          border: pallet.colour1,
          background: pallet.colour1,
          backgroundSelected: `${pallet.colour1}08`,
          borderSelected: `2px solid ${pallet.colour1}`,
          borderUnselected: `1px solid ${pallet.colour1}40`,
          transform: "scale(1.02)",
          boxShadow: `0 4px 12px ${pallet.colour1}20, 0 0 0 1px ${pallet.colour1}10`
        },
        success: {
          border: colors.success,
          background: colors.success,
          backgroundSelected: `${colors.success}08`,
          borderSelected: `2px solid ${colors.success}`,
          borderUnselected: `1px solid ${colors.success}40`,
          transform: "scale(1.02)",
          boxShadow: `0 4px 12px ${colors.success}20, 0 0 0 1px ${colors.success}10`
        },
        accent: {
          border: pallet.colour1,
          background: pallet.colour1,
          backgroundSelected: `${pallet.colour1}08`,
          borderSelected: `2px solid ${pallet.colour1}`,
          borderUnselected: `1px solid ${pallet.colour1}40`,
          transform: "scale(1.02)",
          boxShadow: `0 4px 12px ${pallet.colour1}20, 0 0 0 1px ${pallet.colour1}10`
        }
      },
      unselected: {
        transform: "scale(1)",
        boxShadow: `0 1px 3px ${colors.borderColor1}20`
      },
      selectedIndicator: {
        position: "absolute",
        top: "8px",
        right: "8px",
        width: "24px",
        height: "24px",
        borderRadius: "50%",
        alignItems: "center",
        justifyContent: "center",
        iconColor: colors.fontColor2
      }
    },
    messageCard: {
      container: {
        padding: "14px 18px",
        borderRadius: "8px",
        marginBottom: "16px"
      },
      variants: {
        info: {
          backgroundColor: `${pallet.colour1}10`,
          border: `1px solid ${pallet.colour1}20`,
          borderRadius: "8px"
        },
        success: {
          backgroundColor: `${pallet.colour1}10`,
          border: `1px solid ${pallet.colour1}20`,
          borderRadius: "8px"
        },
        warning: {
          backgroundColor: `${colors.warning}10`,
          border: `1px solid ${colors.warning}20`,
          borderRadius: "8px"
        },
        error: {
          backgroundColor: `${colors.alert}10`,
          border: "none",
          borderRadius: "0"
        }
      },
      text: {
        fontSize: "14px",
        lineHeight: "1.6",
        color: colors.fontColor4,
        textAlign: "left"
      },
      strongText: {
        info: {
          color: colors.fontColor1
        },
        success: {
          color: colors.fontColor1
        },
        warning: {
          color: colors.warning
        },
        error: {
          color: colors.alert
        }
      },
      suggestions: {
        container: {
          marginTop: "16px",
          marginBottom: "16px"
        },
        list: {
          gap: "12px"
        },
        item: {
          fontSize: "14px",
          color: colors.fontColor1
        },
        icon: {
          color: colors.alert,
          marginRight: "12px",
          alignItems: "center"
        }
      },
      noAccounts: {
        container: {
          padding: "40px 20px",
          textAlign: "center"
        },
        text: {
          color: colors.fontColor4,
          width: "100%",
          fontSize: "14px",
          lineHeight: "1.5"
        }
      }
    },
    baseCard: {
      container: {
        padding: "20px",
        borderRadius: "12px",
        backgroundColor: colors.backgroundColor1,
        transition: "all 0.2s ease"
      },
      emphasized: {
        border: `2px solid ${pallet.colour1}`,
        boxShadow: `0 2px 8px ${pallet.colour1}15`
      },
      normal: {
        border: `1px solid ${colors.borderColor1}`,
        boxShadow: "none"
      },
      clickable: {
        cursor: "pointer"
      },
      nonClickable: {
        cursor: "default"
      }
    },
    iconContainer: {
      container: {
        width: "40px",
        height: "40px",
        borderRadius: "8px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0
      },
      emphasized: {
        backgroundColor: pallet.colour1,
        color: colors.fontColor2
      },
      normal: {
        backgroundColor: `${pallet.colour1}10`,
        color: pallet.colour1
      }
    },
    sectionContainer: {
      container: {
        backgroundColor: colors.backgroundColor1,
        padding: "24px",
        borderRadius: "12px",
        border: `2px solid ${pallet.colour1}`,
        boxShadow: `0 2px 8px ${pallet.colour1}15`,
        marginBottom: "24px"
      },
      header: {
        display: "flex",
        alignItems: "center",
        marginBottom: "8px",
        borderBottom: `1px solid ${pallet.colour1}`,
        paddingBottom: "8px"
      },
      icon: {
        color: pallet.colour1,
        marginRight: "16px",
        marginBottom: "8px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      },
      title: {
        fontSize: "18px",
        fontWeight: 600,
        lineHeight: "1.3",
        color: colors.fontColor1,
        marginBottom: "8px"
      }
    },
    stickyButtonContainer: {
      container: {
        position: "fixed",
        bottom: "30px",
        left: "50%",
        transform: "translateX(-50%)",
        width: "100%",
        maxWidth: "380px",
        padding: "24px",
        zIndex: 1000,
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        borderRadius: "12px 12px 0 0",
        display: "flex",
        flexDirection: "column",
        gap: "16px"
      },
      button: {
        padding: "20px 24px",
        fontSize: "16px",
        fontWeight: 600,
        lineHeight: "1",
        borderRadius: "12px",
        width: "100%",
        cursor: "pointer",
        transition: "all 0.2s ease"
      },
      buttonPrimary: {
        border: "none",
        backgroundColor: pallet.colour1,
        color: colors.fontColor2
      },
      buttonSecondary: {
        border: `1px solid ${pallet.colour1}`,
        backgroundColor: "transparent",
        color: pallet.colour1
      },
      buttonDisabled: {
        backgroundColor: colors.backgroundColor4,
        cursor: "not-allowed",
        opacity: 0.6
      }
    },
    methodCard: {
      container: {
        alignItems: "flex-start",
        marginBottom: "16px"
      },
      content: {
        flex: 1,
        marginLeft: "16px"
      },
      title: {
        fontSize: "18px",
        fontWeight: 600,
        lineHeight: "1.3",
        color: colors.fontColor1,
        marginBottom: "8px"
      },
      description: {
        fontSize: "14px",
        fontWeight: 400,
        lineHeight: "1.5",
        color: colors.fontColor4
      },
      checkmark: {
        marginLeft: "8px",
        color: colors.fontColor1
      },
      benefits: {
        gap: "12px",
        paddingTop: "16px",
        borderTop: `1px solid ${colors.borderColor1}`
      },
      benefitItem: {
        marginRight: "8px",
        fontSize: "14px",
        fontWeight: 400,
        lineHeight: "1.5",
        color: colors.fontColor1
      }
    },
    processStepCard: {
      container: {
        gap: "16px"
      },
      step: {
        alignItems: "flex-start",
        marginBottom: "16px"
      },
      iconContainer: {
        marginRight: "8px"
      },
      content: {
        flex: 1
      },
      title: {
        fontSize: "18px",
        fontWeight: 600,
        lineHeight: "1.3",
        color: colors.fontColor1
      },
      description: {
        fontSize: "14px",
        fontWeight: 400,
        lineHeight: "1.5",
        color: colors.fontColor4
      }
    },
    requirementCard: {
      container: {
        padding: "20px",
        backgroundColor: `${pallet.colour1}05`,
        borderRadius: "12px",
        border: `1px solid ${pallet.colour1}15`,
        alignItems: "flex-start"
      },
      iconContainer: {
        marginRight: "12px",
        color: pallet.colour1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      },
      content: {
        flex: 1
      },
      title: {
        fontSize: "18px",
        fontWeight: 600,
        lineHeight: "1.3",
        color: colors.fontColor1,
        marginBottom: "4px"
      },
      description: {
        fontSize: "14px",
        fontWeight: 400,
        lineHeight: "1.5",
        color: colors.fontColor4
      },
      checkIcon: {
        backgroundColor: pallet.colour1,
        borderRadius: "50%",
        width: "20px",
        height: "20px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: colors.fontColor2
      },
      countBadge: {
        backgroundColor: pallet.colour1,
        color: colors.fontColor2,
        padding: "6px 10px",
        borderRadius: "16px",
        fontSize: "13px",
        fontWeight: 600,
        minWidth: "28px",
        textAlign: "center"
      }
    },
    manualUploadCard: {
      container: {
        marginBottom: "16px"
      },
      statusContainer: {
        padding: "14px 18px",
        backgroundColor: `${pallet.colour1}10`,
        borderRadius: "8px",
        marginBottom: "16px"
      },
      statusText: {
        fontSize: "14px",
        lineHeight: "1.6",
        color: colors.fontColor4,
        textAlign: "left"
      },
      statusStrong: {
        color: colors.fontColor1
      },
      errorContainer: {
        padding: "14px 18px",
        backgroundColor: `${colors.alert}10`,
        borderRadius: "8px",
        border: `1px solid ${colors.alert}20`,
        marginBottom: "16px"
      },
      errorText: {
        fontSize: "14px",
        lineHeight: "1.6",
        color: colors.fontColor4,
        textAlign: "left"
      },
      errorStrong: {
        color: colors.alert
      },
      fileDisplayCard: {
        backgroundColor: colors.backgroundColor6,
        border: `1px solid ${colors.borderColor1}`,
        borderRadius: "8px",
        padding: "16px",
        marginBottom: "12px"
      },
      fileDisplayVariants: {
        completed: {
          backgroundColor: colors.backgroundColor6,
          border: `1px solid ${pallet.colour1}30`,
          accent: pallet.colour1
        },
        uploading: {
          backgroundColor: colors.backgroundColor7,
          border: `1px solid ${colors.warning}30`,
          accent: colors.warning
        },
        default: {
          backgroundColor: colors.backgroundColor6,
          border: `1px solid ${colors.borderColor1}`,
          accent: pallet.colour1
        }
      },
      fileName: {
        fontSize: "16px",
        fontWeight: 600,
        color: colors.fontColor1,
        marginBottom: "4px"
      },
      fileSize: {
        fontSize: "14px",
        color: colors.fontColor4
      },
      progressBar: {
        backgroundColor: colors.backgroundColor4,
        borderRadius: "10px",
        height: "8px",
        overflow: "hidden",
        marginTop: "12px"
      },
      progressFill: {
        height: "100%",
        transition: "width 0.3s ease"
      },
      statusIcon: {
        width: "32px",
        height: "32px",
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginLeft: "auto"
      },
      removeButton: {
        backgroundColor: "transparent",
        border: "none",
        color: colors.fontColor4,
        cursor: "pointer",
        padding: "4px",
        borderRadius: "4px",
        marginLeft: "8px"
      }
    },
    consentFlow: {
      stepContainer: {
        alignItems: "center",
        flexShrink: 0
      },
      logoContainer: {
        width: "56px",
        height: "56px",
        borderRadius: "50%",
        backgroundColor: colors.backgroundColor1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: "0"
      },
      logoImage: {
        width: "64px",
        height: "64px",
        objectFit: "contain"
      },
      stepIndicator: {
        width: "20px",
        height: "20px",
        borderRadius: "50%",
        backgroundColor: pallet.colour1,
        marginTop: "16px"
      },
      connectingLine: {
        width: "2px",
        height: "28px",
        backgroundColor: pallet.colour1
      },
      stepText: {
        fontSize: "16px",
        fontWeight: 700,
        lineHeight: "20px",
        color: colors.fontColor1
      },
      stepDescription: {
        fontSize: "12px",
        fontWeight: 400,
        color: colors.fontColor4,
        lineHeight: "16px"
      },
      modalContainer: {
        backgroundColor: colors.backgroundColor1,
        borderRadius: "16px",
        padding: "24px",
        maxWidth: "500px",
        minHeight: "200px"
      },
      countdownText: {
        fontSize: "14px",
        color: colors.fontColor1,
        fontWeight: 500
      }
    },
    landingPage: {
      container: {
        marginTop: "28px",
        marginBottom: "28px"
      },
      heroTitle: {
        fontSize: "clamp(28px, 6vw, 32px)",
        fontWeight: 700,
        color: colors.fontColor1,
        margin: "0 0 12px 0",
        lineHeight: "1.2"
      },
      heroSubtitle: {
        fontSize: "18px",
        lineHeight: "1.6",
        color: colors.fontColor4,
        margin: 0,
        fontWeight: 400,
        opacity: 0.85
      },
      welcomeMessage: {
        fontSize: "16px",
        color: colors.fontColor1,
        margin: 0,
        fontWeight: 500
      },
      instructionText: {
        fontSize: "14px",
        color: colors.fontColor4,
        lineHeight: "1.5",
        margin: 0
      },
      iconSize: {
        width: "60px",
        height: "60px"
      }
    },
    pageHeader: {
      container: {
        width: "100%",
        maxWidth: "480px",
        margin: "0 auto"
      },
      titleRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: "20px",
        gap: "16px"
      },
      title: {
        fontSize: "clamp(24px, 5vw, 28px)",
        fontWeight: 600,
        margin: 0,
        lineHeight: "1.2",
        color: colors.fontColor1,
        flex: 1,
        minWidth: 0
      },
      identityTitle: {
        fontSize: "clamp(20px, 5vw, 24px)",
        fontWeight: 700,
        margin: "0 0 12px 0",
        lineHeight: "1.2",
        color: pallet.colour1,
        textAlign: "left"
      },
      message: {
        color: colors.fontColor4,
        fontSize: "16px",
        lineHeight: "1.6",
        margin: 0,
        fontWeight: 400,
        textAlign: "left",
        whiteSpace: "pre-line"
      },
      logo: {
        width: "56px",
        height: "56px",
        objectFit: "contain",
        flexShrink: 0,
        borderRadius: "12px",
        backgroundColor: colors.backgroundColor1,
        padding: "8px",
        boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.08)",
        border: `1px solid ${colors.borderColor1}`
      }
    },
    gifPlayer: {
      modal: {
        overlayBackground: "rgba(0, 0, 0, 0.8)",
        zIndex: 1000
      },
      thumbnail: {
        width: "60px",
        height: "100px",
        playButtonSize: "20px",
        playButtonBackground: "rgba(0, 0, 0, 0.6)"
      }
    },
    monthBadge: {
      container: {
        border: `2px solid ${pallet.colour1}`,
        borderRadius: "8px",
        padding: "12px 16px",
        textAlign: "center",
        backgroundColor: colors.backgroundColor1,
        minWidth: "80px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center"
      },
      monthText: {
        fontSize: "14px",
        fontWeight: 700,
        color: pallet.colour1,
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        marginBottom: "4px"
      },
      yearText: {
        fontSize: "13px",
        fontWeight: 500,
        color: colors.fontColor4
      }
    },
    docRequirementSubHeader: {
      fontSize: "14px",
      fontWeight: 600,
      color: pallet.colour1,
      marginBottom: "12px"
    },
    documentReminder: {
      header: {
        fontSize: "16px",
        fontWeight: 600,
        color: colors.fontColor1,
        margin: "0 0 0 8px"
      },
      requirementBadge: {
        fontSize: "14px",
        fontWeight: 600,
        padding: "6px",
        borderRadius: "6px",
        display: "block",
        width: "100%",
        marginBottom: "5px"
      },
      requirementBadgeRequired: {
        color: colors.alert,
        backgroundColor: colors.alertBackground,
        border: `1px solid ${colors.alertBorder}`
      },
      requirementBadgeOptional: {
        color: colors.success,
        backgroundColor: colors.successBackground,
        border: `1px solid ${colors.successBorder}`
      },
      expandButton: {
        backgroundColor: `${pallet.colour1}10`,
        border: "none",
        padding: "8px 0",
        color: pallet.colour1,
        fontSize: "14px",
        fontWeight: 600,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        marginBottom: "15px"
      },
      detailCard: {
        padding: "15px",
        backgroundColor: colors.backgroundColor1,
        borderRadius: "12px",
        border: `1px solid ${colors.borderColor1}`
      },
      detailCardTitle: {
        fontSize: "15px",
        fontWeight: 600,
        color: pallet.colour1,
        marginBottom: "4px"
      },
      detailCardText: {
        fontSize: "14px",
        color: colors.fontColor4,
        marginBottom: "2px"
      },
      detailCardSubText: {
        fontSize: "13px",
        color: colors.fontColor4,
        marginBottom: "2px"
      },
      detailCardLastText: {
        fontSize: "13px",
        color: colors.fontColor4
      },
      boldValue: {
        fontWeight: 600
      },
      helpfulTipDivider: {
        marginTop: "16px",
        paddingTop: "16px",
      },
      helpfulTipHeading: {
        fontSize: "14px",
        fontWeight: 600,
        color: colors.fontColor1,
        marginLeft: "8px"
      },
      helpfulTipText: {
        fontSize: "14px",
        lineHeight: "1.6",
        color: colors.fontColor4,
        opacity: 0.85
      }
    }
  }
}
